import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useState, useCallback } from 'react';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { useDropzone } from 'react-dropzone';
import { FaTrash, FaUpload } from 'react-icons/fa';

interface CreateAudienceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateAudienceModal = ({
  isOpen,
  onClose,
}: CreateAudienceModalProps) => {
  const toast = useToast();
  const [twitterUsername, setTwitterUsername] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
    },
  });

  const handleSubmit = async () => {
    // Validate inputs
    if (!twitterUsername.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a Twitter username',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedFile) {
      toast({
        title: 'Error',
        description: 'Please upload a PDF file with follower handles',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Here you would implement the API call to create the audience
      // For example:
      // const formData = new FormData();
      // formData.append('twitterUsername', twitterUsername);
      // formData.append('file', selectedFile);
      // await fetch('/api/create-audience', { method: 'POST', body: formData });

      toast({
        title: 'Success',
        description: 'Audience created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form and close modal
      setTwitterUsername('');
      setSelectedFile(null);
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create audience',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTwitterUsername('');
    setSelectedFile(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={HelivanColor.background.primary}>
        <ModalHeader>
          <HelivanText variant="largeSubheader">Create Audience</HelivanText>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="start" width="100%">
            {/* Twitter Username Input */}
            <FormControl isRequired>
              <FormLabel>
                <HelivanText variant="subheader">Twitter Username</HelivanText>
              </FormLabel>
              <Input
                placeholder="@username"
                value={twitterUsername}
                onChange={(e) => setTwitterUsername(e.target.value)}
                bg={HelivanColor.background.secondary}
                borderColor={HelivanColor.border.primary}
                _hover={{ borderColor: HelivanColor.border.hover }}
                _focus={{ borderColor: HelivanColor.border.hover }}
              />
            </FormControl>

            {/* PDF Upload */}
            <FormControl isRequired>
              <FormLabel>
                <HelivanText variant="subheader">
                  Upload Follower Handles (PDF)
                </HelivanText>
              </FormLabel>
              <Box position="relative">
                <Box
                  {...getRootProps()}
                  p={6}
                  border="2px dashed"
                  borderColor={
                    selectedFile
                      ? 'green.500'
                      : isDragActive
                        ? 'blue.500'
                        : HelivanColor.border.primary
                  }
                  borderRadius="md"
                  textAlign="center"
                  cursor="pointer"
                  bg={
                    selectedFile
                      ? 'green.50'
                      : HelivanColor.background.secondary
                  }
                  _hover={{
                    borderColor: selectedFile
                      ? 'green.600'
                      : HelivanColor.border.hover,
                  }}
                >
                  <input {...getInputProps()} />
                  <Icon
                    as={FaUpload}
                    w={8}
                    h={8}
                    color={selectedFile ? 'green.500' : 'gray.400'}
                    mb={2}
                  />
                  {selectedFile ? (
                    <VStack spacing={2}>
                      <Text fontWeight="bold" color="green.700">
                        File selected: {selectedFile.name}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Click to replace or drag a new file
                      </Text>
                    </VStack>
                  ) : (
                    <VStack spacing={2}>
                      <Text>
                        {isDragActive
                          ? 'Drop the file here'
                          : 'Drag and drop a file here, or click to select'}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Supported format: PDF
                      </Text>
                    </VStack>
                  )}
                </Box>
                {selectedFile && (
                  <IconButton
                    aria-label="Clear file"
                    icon={<FaTrash />}
                    size="sm"
                    colorScheme="red"
                    position="absolute"
                    top={2}
                    right={2}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  />
                )}
              </Box>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Flex width="100%" justifyContent="space-between">
            <HelivanButton
              variant="ghost"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="action"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={
                !twitterUsername.trim() || !selectedFile || isSubmitting
              }
            >
              Create
            </HelivanButton>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
